import { useCallback, useEffect, useMemo, useState } from "react"
import { PositionOpen } from "sdk-react/clearingHouse/usePositionOpen"
import { OrderType } from "sdk-react/limitOrder/types"
import { CreateLimitOrder } from "sdk-react/limitOrder/useCreateLimitOrder"
import { createContainer } from "unstated-next"

export const CreateOrder = createContainer(useCreateOrder)

function useCreateOrder() {
    const [orderType, setOrderType] = useState<OrderType>(OrderType.MARKET)
    const isOrderTypeMarket = [OrderType.MARKET].includes(orderType)
    const isOrderTypeLimit = [OrderType.LIMIT].includes(orderType)
    const isOrderTypeStop = [OrderType.STOP_LIMIT, OrderType.TAKE_PROFIT_LIMIT].includes(orderType)

    const marketOrderState = PositionOpen.useContainer()
    const limitOrderState = CreateLimitOrder.useContainer()
    const orderState = isOrderTypeLimit || isOrderTypeStop ? limitOrderState : marketOrderState
    const isStopOrderSupported = limitOrderState?.isStopOrderSupported

    const updateOrderType = useCallback(
        (nextOrderType: OrderType) => {
            setOrderType(nextOrderType)

            if (nextOrderType !== OrderType.MARKET) {
                limitOrderState.setSide(marketOrderState.side)
                limitOrderState.setIsReduceOnly(false)
                limitOrderState.updateLimitPrice(marketOrderState.marketPriceString || "")
            } else {
                // TODO:
                // market order
                // marketOrderState.copyStateFrom(limitOrderState)
            }
        },
        [limitOrderState, marketOrderState.marketPriceString, marketOrderState.side],
    )

    useEffect(() => {
        if (!isStopOrderSupported) {
            setOrderType(OrderType.MARKET)
        }
    }, [isStopOrderSupported])

    const priceString = useMemo(() => {
        return isOrderTypeMarket ? marketOrderState.marketPriceString : limitOrderState.limitPriceString
    }, [isOrderTypeMarket, marketOrderState.marketPriceString, limitOrderState.limitPriceString])

    const updatePrice = useMemo(() => {
        return isOrderTypeMarket ? () => null : limitOrderState.updateLimitPrice
    }, [isOrderTypeMarket, limitOrderState.updateLimitPrice])

    const triggerPriceString = useMemo(() => {
        return isOrderTypeMarket ? undefined : limitOrderState.triggerPriceString
    }, [isOrderTypeMarket, limitOrderState.triggerPriceString])

    const updateTriggerPrice = useMemo(() => {
        return isOrderTypeMarket ? () => null : limitOrderState.updateTriggerPrice
    }, [isOrderTypeMarket, limitOrderState.updateTriggerPrice])

    const handleSubmit = useMemo(() => {
        return isOrderTypeMarket ? marketOrderState.handleSubmit : () => limitOrderState.handleSubmit(orderType)
    }, [isOrderTypeMarket, limitOrderState, marketOrderState.handleSubmit, orderType])

    return {
        ...orderState,
        positionDraft: isOrderTypeMarket ? marketOrderState.positionDraft : undefined,
        isUsingOtcEntryPrice: isOrderTypeMarket ? marketOrderState.isUsingOtcEntryPrice : false,
        isStopOrderSupported,
        handleSubmit,
        updateOrderType,
        orderType,
        isOrderTypeMarket,
        isOrderTypeLimit,
        isOrderTypeStop,
        priceString,
        updatePrice,
        triggerPriceString,
        updateTriggerPrice,
    }
}
